import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { Add, Clear } from '@mui/icons-material';
import {
  availableOperators,
  customRuleMapping,
  type ObjectType,
  type RuleDocument,
  type RuleGroupDocument,
  type RuleOptions,
  ruleMapping,
  ValueTypes,
} from '@onevesthq/ov-conditions';
import { Box } from '@ovComponents/1-primative/box/box';
import { Button } from '@ovComponents/2-component/button/button';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Selector } from '@ovComponents/2-component/selector/selector';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rule } from './components/rule';

export const conditionGraphql = `
  condition {
    ruleGroup {
      combinator
      rules {
        combinator
        field
        operator
        value
        customFieldKey
        customFieldType
        objectType
        rules {
          combinator
          field
          operator
          value
          customFieldKey
          customFieldType
          objectType
          rules {
            combinator
            field
            operator
            value
            customFieldKey
            customFieldType
            objectType
          }
        }
      }
    }
  }
`;

export const FETCH_CUSTOM_FIELDS = gql`
  query fetchCustomFields($input: FetchCustomFieldsInput!) {
    fetchCustomFields(input: $input) {
      customFields{
        id
        key
        type
        format
        objectType
        workflowStep
        translatedName{ ${TRANSLATED_STRING_FIELDS} }
        selectOptions{
          value
          displayText { ${TRANSLATED_STRING_FIELDS} }
        }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const MAX_DEPTH = 3;

export const RuleBuilder = ({
  ruleGroup,
  setRuleGroup,
  objectType,
  removeRuleGroup,
  depth = 0,
}: {
  ruleGroup: RuleGroupDocument;
  setRuleGroup: (r: RuleGroupDocument) => void;
  objectType: ObjectType;
  removeRuleGroup?: () => void;
  depth?: number;
}) => {
  const { t } = useTranslation('components');
  const { activeOrganization } = useContext(UserContext);
  const [availableRules, setAvailableRules] = useState<RuleOptions[]>([]);
  const [localRuleGroup, setLocalRuleGroup] =
    useState<RuleGroupDocument>(ruleGroup);

  const { data } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id ?? undefined,
        },
        pagination: {
          offSet: 0,
          perPage: 1000,
        },
      },
    },
  });

  useEffect(() => {
    setLocalRuleGroup(ruleGroup);
  }, [ruleGroup]);

  useEffect(() => {
    if (data && objectType) {
      const rs: RuleOptions[] = customRuleMapping(
        objectType,
        data.fetchCustomFields.customFields,
      );
      setAvailableRules(rs);
    }
  }, [objectType, data]);

  const addRule = () => {
    const firstRule = availableRules[0];
    if (!firstRule) return;

    setRuleGroup({
      ...localRuleGroup,
      rules: [
        ...localRuleGroup.rules,
        {
          field: firstRule.name,
          operator: availableOperators(
            ruleMapping[objectType]?.find(
              (x: RuleOptions) => x.name === firstRule.name,
            )?.valueType || ValueTypes.TEXT,
          )[0],
          value: '',
          objectType,
        },
      ],
    });
  };

  const removeRule = (index: number) => {
    const rs = [...localRuleGroup.rules];
    rs.splice(index, 1);
    setRuleGroup({ ...localRuleGroup, rules: rs });
  };

  const addRuleGroup = () => {
    const newRules = [...localRuleGroup.rules];
    newRules.push({
      combinator: 'and',
      rules: [],
    });
    setRuleGroup({ ...localRuleGroup, rules: newRules });
  };

  if (!localRuleGroup) {
    return null;
  }

  return (
    <Box display='flex' flexDirection='column' gap={1}>
      <Box display='flex' gap={1} justifyContent='space-between'>
        <Box display='flex' gap={1}>
          <Selector
            options={[
              { label: t('ruleBuilder.and'), type: 'and' },
              { label: t('ruleBuilder.or'), type: 'or' },
            ]}
            selectedOptionType={localRuleGroup.combinator}
            onChange={(event) => {
              setRuleGroup({
                ...localRuleGroup,
                combinator: event.target.value,
              });
            }}
          />
          <Button
            label={t('ruleBuilder.addRule')}
            leadingIcon={Add}
            variant='text'
            size='sm'
            onClick={addRule}
            disabled={availableRules.length === 0}
          />
          {depth < MAX_DEPTH && (
            <Button
              label={t('ruleBuilder.addGroup')}
              leadingIcon={Add}
              variant='text'
              size='sm'
              onClick={addRuleGroup}
            />
          )}
        </Box>
        {removeRuleGroup && (
          <Button
            onClick={removeRuleGroup}
            label={t('ruleBuilder.removeRuleGroup')}
            variant='text'
            leadingIcon={Clear}
            size='sm'
          />
        )}
      </Box>
      {(localRuleGroup.rules || []).length > 0 &&
        (localRuleGroup.rules || []).map((rule, index) => (
          <Box key={index} display='flex'>
            {'field' in rule && rule.field && (
              <Rule
                rule={rule}
                updateRule={(r: RuleDocument) => {
                  const rs = [...localRuleGroup.rules];
                  rs[index] = r;
                  setRuleGroup({ ...localRuleGroup, rules: rs });
                }}
                availableRules={availableRules}
                objectType={objectType}
                removeRule={() => removeRule(index)}
              />
            )}
            {'rules' in rule && rule.rules && (
              <Card variant='outlined' sx={{ width: '100%' }}>
                <CardContent>
                  <RuleBuilder
                    ruleGroup={rule}
                    objectType={objectType}
                    removeRuleGroup={() => removeRule(index)}
                    setRuleGroup={(r: RuleGroupDocument) => {
                      const rs = [...localRuleGroup.rules];
                      rs[index] = r;
                      setRuleGroup({ ...localRuleGroup, rules: rs });
                    }}
                    depth={depth + 1}
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        ))}
    </Box>
  );
};
